import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { applyLang, getCurrentLang } from '../utils/i18n';
import {
  archivesApi,
  ARCHIVE_TYPES, TYPE_LABEL, TYPE_GLYPH, LANGUAGE_LABEL,
} from '../services/archivesApi';
import './Bibliotheque.css';

function fmtDuration(secs) {
  if (!secs) return '';
  if (secs >= 3600) {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return `${h}h${m ? String(m).padStart(2, '0') : ''}`;
  }
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const BibliothequeNumerique = () => {
  const [filter, setFilter] = useState('all');
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.bib-page .reveal').forEach((el) => io.observe(el));
    applyLang(getCurrentLang());
    return () => io.disconnect();
  });

  useEffect(() => {
    let cancelled = false;
    archivesApi.list({ limit: 200 })
      .then((data) => { if (!cancelled) { setArchives(data.items); setLoading(false); } })
      .catch(() => { if (!cancelled) { setArchives([]); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => {
    const c = { all: archives.length };
    for (const a of archives) c[a.type] = (c[a.type] || 0) + 1;
    return c;
  }, [archives]);

  const featured = useMemo(() => archives.filter((a) => a.featured).slice(0, 2), [archives]);

  const visible = useMemo(() => {
    if (filter === 'all') return archives;
    return archives.filter((a) => a.type === filter);
  }, [filter, archives]);

  // Stats
  const oldestYear = useMemo(() => {
    const years = archives.map((a) => a.year).filter(Boolean);
    return years.length > 0 ? Math.min(...years) : null;
  }, [archives]);

  const collections = useMemo(() => {
    return new Set(archives.map((a) => a.collection).filter(Boolean)).size;
  }, [archives]);

  const imgFor = (a) => a.imageUrl || a.previewUrl || `https://picsum.photos/seed/${a.imageSeed || `cb-arc-${a.id}`}/900/680`;

  return (
    <main className="bib-page">
      {/* ============== HERO ============== */}
      <header className="bib-hero">
        <div className="bib-hero-bg" />
        <div className="bib-hero-grain" />
        <div className="bib-hero-watermark" aria-hidden="true">Archives</div>

        <div className="wrap bib-hero-inner reveal">
          <span className="bib-hero-eyebrow">Bibliothèque numérique · Archives Layène</span>
          <h1>
            La mémoire<br />
            <span className="ital">du village.</span>
          </h1>
          <p className="lede">
            Manuscrits fondateurs, photographies anciennes, enregistrements de tassou, cartes du
            débarcadère, captations vidéo des Khalifes — toutes les archives layènes restaurées,
            numérisées et accessibles à tous.
          </p>

          <div className="bib-hero-foot">
            <div><b>{loading ? '—' : archives.length}</b><span>Documents</span></div>
            <div><b>{collections}</b><span>Collections</span></div>
            <div><b>{oldestYear || '—'}</b><span>Plus ancien</span></div>
            <div><b>{(counts.audio || 0) + (counts.video || 0)}</b><span>Médias audio/vidéo</span></div>
          </div>
        </div>
      </header>

      {/* ============== FILTRES ============== */}
      <div className="bib-filters">
        <div className="wrap">
          <span className="lab">Types —</span>
          {ARCHIVE_TYPES.map((t) => (
            <button
              key={t.id}
              className={`bib-chip${filter === t.id ? ' on' : ''}`}
              onClick={() => setFilter(t.id)}
            >
              <span className="glyph">{t.glyph}</span>
              {t.lbl}
              <span className="ct">{counts[t.id] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ============== À LA UNE ============== */}
      {featured.length > 0 && (
        <section className="bib-featured">
          <div className="wrap">
            <div className="bib-head reveal">
              <div>
                <span className="eyebrow">Pièces majeures</span>
                <h2>Les trésors<br /><span className="ital">de la collection.</span></h2>
              </div>
            </div>

            <div className="fea-grid">
              {featured.map((a) => (
                <article key={a.id} className="fea-archive reveal">
                  <div className="img" style={{ backgroundImage: `url('${imgFor(a)}')` }} />
                  <div className="grad" />
                  <div className="top">
                    <span className="type-pill">
                      <span className="gl">{TYPE_GLYPH[a.type]}</span>
                      {TYPE_LABEL[a.type]}
                    </span>
                    {a.year && <span className="year">{a.year}</span>}
                  </div>
                  <div className="body">
                    <h3>{a.title}</h3>
                    {a.summary && <p>{a.summary}</p>}
                    <div className="meta">
                      {a.author && <span>par {a.author}</span>}
                      {a.collection && <span>{a.collection}</span>}
                      {a.pageCount > 0 && <span>{a.pageCount} pages</span>}
                      {a.duration > 0 && <span>{fmtDuration(a.duration)}</span>}
                      {a.fileSize && <span>{a.fileSize}</span>}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============== COLLECTION COMPLÈTE ============== */}
      <section className="bib-list">
        <div className="wrap">
          <div className="bib-head reveal">
            <div>
              <span className="eyebrow">
                {filter === 'all' ? 'Toutes les archives' : TYPE_LABEL[filter]}
                {' · '}{visible.length} document{visible.length > 1 ? 's' : ''}
              </span>
              <h2>Catalogue <span className="ital">complet.</span></h2>
            </div>
          </div>

          {loading && (
            <p style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>
              Chargement des archives…
            </p>
          )}

          {!loading && visible.length === 0 && (
            <p style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
              Aucune archive {filter !== 'all' ? `de type « ${TYPE_LABEL[filter]} »` : ''} disponible.
            </p>
          )}

          {!loading && visible.length > 0 && (
            <div className="arc-grid">
              {visible.map((a) => (
                <article key={a.id} className="arc-card reveal">
                  <div className="ph">
                    <div className="img" style={{ backgroundImage: `url('${imgFor(a)}')` }} />
                    <span className={`type ${a.type}`}>
                      <span className="gl">{TYPE_GLYPH[a.type]}</span>
                      {TYPE_LABEL[a.type]}
                    </span>
                    {a.year && <span className="year">{a.year}</span>}
                  </div>
                  <div className="body">
                    {a.collection && <span className="collection">{a.collection}</span>}
                    <h4>{a.title}</h4>
                    {a.summary && <p className="summary">{a.summary}</p>}
                    {a.tags && a.tags.length > 0 && (
                      <div className="tags">
                        {a.tags.slice(0, 4).map((t, i) => <span key={i}>#{t}</span>)}
                      </div>
                    )}
                    <div className="meta">
                      {a.author && <span>Auteur · <b>{a.author}</b></span>}
                      {a.language && a.language !== 'na' && <span>{LANGUAGE_LABEL[a.language]}</span>}
                      {a.pageCount > 0 && <span>{a.pageCount} p.</span>}
                      {a.duration > 0 && <span>{fmtDuration(a.duration)}</span>}
                      {a.fileSize && <span>{a.fileSize}</span>}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============== CTA ============== */}
      <section className="bib-cta">
        <div className="wrap reveal">
          <span className="eyebrow">Contribuer aux archives</span>
          <h3>Vous avez un document ou une photo <span className="ital">à transmettre ?</span></h3>
          <p>
            Manuscrit familial, enregistrement de cérémonie, photo ancienne — chaque pièce
            enrichit la mémoire collective. Les archives layènes vérifient et numérisent.
          </p>
          <div className="bib-cta-actions">
            <Link to="/inscription" className="btn btn-primary">
              <span>Proposer un document</span>
              <span className="arrow">→</span>
            </Link>
            <Link to="/histoire" className="btn btn-ghost">
              <span>Histoire du village</span>
              <span className="arrow">↗</span>
            </Link>
            <Link to="/spiritualite" className="btn btn-ghost">
              <span>Spiritualité Layène</span>
              <span className="arrow">↗</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BibliothequeNumerique;
